import {
  AUDIO_BELL_LEVEL,
  AUDIO_BELL_MOD_INDEX,
  AUDIO_BELL_MOD_RATIO,
  AUDIO_BELL_TRANSPOSE,
  AUDIO_DENSITY_DETUNE_CENTS,
  AUDIO_DENSITY_DUCK_FLOOR,
  AUDIO_DENSITY_GAIN_FLOOR,
  AUDIO_DENSITY_RATE_HIGH,
  AUDIO_DENSITY_RATE_LOW,
  AUDIO_DENSITY_TRANSIENT,
  AUDIO_MASTER_GAIN,
  AUDIO_WOOD_DECAY_S,
  CLOCK_PERIOD_DEFAULT,
  CLOCK_PERIOD_MAX,
  CLOCK_PERIOD_MIN,
  DEFAULT_FRICTION,
  DEFAULT_MASS,
  DEFAULT_RESTITUTION,
  EMIT_CONE_HALF_ANGLE,
  EMIT_RATE_DEFAULT,
  EMIT_SPEED_MAX,
  EMIT_SPEED_MIN,
  EMITTER_POSITION,
  FALL_KILL_DIST_DEFAULT,
  FALL_KILL_DIST_MAX,
  FALL_KILL_DIST_MIN,
  FALL_KILL_LIFE_DIV_DEFAULT,
  FALL_KILL_LIFE_DIV_MAX,
  FALL_KILL_LIFE_DIV_MIN,
  GRAVITY,
  GRAVITY_Y_MAX,
  GRAVITY_Y_MIN,
  KEY_LIGHT_DIR_X,
  KEY_LIGHT_DIR_Y,
  LIFESPAN_MAX,
  LIFESPAN_MIN,
  PARTICLE_WORLD_SIZE,
  REVERB_AMOUNT_DEFAULT,
  SHADOW_MAP_SIZE,
} from "./constants";
import type { Language } from "./i18n";
import { COLOR_SETS, ColorSet, OBJECT_MATERIALS, ObjectMaterial, ObjectType } from "./objects";
import { PARTICLE_MATERIALS, ParticleMaterial } from "./particles";
import type { ViewMode } from "./scene";
import { EDIT_SNAP_VALUES, EditSnap } from "./snap";
import { WALL_DEFAULT_PARAMS, WALL_PRESETS, WallParams, WallPreset, wallParamsToPreset, wallPresetParams } from "./wallMaps";

export const FEATURE_CATEGORIES = ["physics", "3d", "node"] as const;
export type FeatureCategory = (typeof FEATURE_CATEGORIES)[number];

export interface Settings {
  viewMode: ViewMode;
  pointSize: number;
  gravityY: number;
  fallKillDistance: number;
  fallKillLifeDivisor: number;
  shape: ObjectType;
  bounce: number;
  friction: number;
  mass: number;
  volume: number;
  woodDecayS: number;
  bellTranspose: number;
  bellModRatio: number;
  bellModIndex: number;
  bellLevel: number;
  densityLow: number;
  densityHigh: number;
  densityTransient: number;
  densityDetune: number;
  densityVoiceGain: number;
  densityDuck: number;
  densityReverb: number;
  showNoteName: boolean;
  colorSet: ColorSet;
  objectMaterial: ObjectMaterial;
  particleMaterial: ParticleMaterial;
  clockPeriod: number;
  editSnap: EditSnap;
  foldouts: Record<string, boolean>;
  features: Record<FeatureCategory, boolean>;
  simpleMode: boolean;
  skipSimpleModeConfirm: boolean;
  cameraEnabled: boolean;
  objectPlacement: boolean;
  communitySceneCameraEnabled: boolean;
  communitySceneObjectPlacement: boolean;
  wallLightX: number;
  wallLightY: number;
  wallShadowRes: number;
  stopOnBlur: boolean;
  language: Language;
}

export const DEFAULT_SETTINGS: Settings = {
  viewMode: "2d",
  pointSize: PARTICLE_WORLD_SIZE,
  gravityY: GRAVITY.y,
  fallKillDistance: FALL_KILL_DIST_DEFAULT,
  fallKillLifeDivisor: FALL_KILL_LIFE_DIV_DEFAULT,
  shape: "box",
  bounce: DEFAULT_RESTITUTION,
  friction: DEFAULT_FRICTION,
  mass: DEFAULT_MASS,
  volume: AUDIO_MASTER_GAIN,
  woodDecayS: AUDIO_WOOD_DECAY_S,
  bellTranspose: AUDIO_BELL_TRANSPOSE,
  bellModRatio: AUDIO_BELL_MOD_RATIO,
  bellModIndex: AUDIO_BELL_MOD_INDEX,
  bellLevel: AUDIO_BELL_LEVEL,
  densityLow: AUDIO_DENSITY_RATE_LOW,
  densityHigh: AUDIO_DENSITY_RATE_HIGH,
  densityTransient: AUDIO_DENSITY_TRANSIENT,
  densityDetune: AUDIO_DENSITY_DETUNE_CENTS,
  densityVoiceGain: AUDIO_DENSITY_GAIN_FLOOR,
  densityDuck: AUDIO_DENSITY_DUCK_FLOOR,
  densityReverb: REVERB_AMOUNT_DEFAULT,
  showNoteName: true,
  colorSet: "pitch",
  objectMaterial: "matte",
  particleMaterial: "matte",
  clockPeriod: CLOCK_PERIOD_DEFAULT,
  editSnap: "0.1",
  foldouts: {},
  features: { physics: false, "3d": false, node: false },
  simpleMode: false,
  skipSimpleModeConfirm: false,
  cameraEnabled: true,
  objectPlacement: true,
  communitySceneCameraEnabled: true,
  communitySceneObjectPlacement: true,
  wallLightX: KEY_LIGHT_DIR_X,
  wallLightY: KEY_LIGHT_DIR_Y,
  wallShadowRes: SHADOW_MAP_SIZE,
  stopOnBlur: false,
  language: "en",
};

const STORAGE_KEY = "resonella.settings";
const SETTINGS_VERSION = 22;

/**
 * The settings that belong to the piece (the "作品設定") and travel inside a
 * saved / exported scene: gravity, the room clock period, the feature-category
 * gates, the simplified-mode flag, and the 2D/3D view mode (they shape the
 * experience the author intends, so they ride with the piece; the camera pose
 * travels alongside in the scene's `camera` field). Object placement and
 * camera-control are session-only top-bar toggles, never serialized:
 * `objectPlacement`, `cameraEnabled`, `communitySceneCameraEnabled`,
 * `communitySceneObjectPlacement`, `wallLightX`, `wallLightY`, and
 * `wallShadowRes` are absent from this list. Master volume, the
 * developer-only
 * voicing controls (Timbre / Density / fall-kill, exposed only under `?debug`),
 * the stop-in-background flag, and every editor / device-local field (snap,
 * foldouts, note-name labels, materials, point size, scene transpose, the
 * new-object spawn defaults shape/bounce/friction/mass, …) stay in this device's
 * localStorage and are never written into a scene — loading a scene must not
 * overwrite them.
 */
export const SCENE_SETTING_KEYS: readonly (keyof Settings)[] = [
  "gravityY",
  "clockPeriod",
  "features",
  "simpleMode",
  "viewMode",
];

/** The scene-setting subset of a Settings object, the only settings written into a saved / exported scene. */
export function sceneSettings(settings: Settings): Partial<Settings> {
  const out: Record<string, unknown> = {};
  for (const key of SCENE_SETTING_KEYS) {
    out[key] = settings[key];
  }
  return out as Partial<Settings>;
}

/**
 * The scene-setting keys mirrored live to other room participants. A subset of
 * SCENE_SETTING_KEYS: `viewMode` is intentionally excluded so the 2D/3D choice
 * stays a per-participant viewing preference rather than being forced on the whole
 * room. A key absent here never rides in a settings op, so editing it locally
 * produces no diff and receivers keep their own value. It still travels in a saved
 * / exported scene (that is SCENE_SETTING_KEYS, unaffected).
 *
 * Personal "個人設定" (Personal Settings) keys are deliberately NOT here and are
 * never room-synced — they are per-device / per-viewer preferences: the wall key
 * light direction (`wallLightX`/`wallLightY`) and shadow resolution
 * (`wallShadowRes`) affect rendering cost and taste, the camera / object-placement
 * / language / snap toggles are local controls. Only the shared *scene* surface
 * syncs: these four keys plus the wallpaper preset, which rides separately inside
 * the settings op's `env` blob (the wall pattern/palette identity), not the
 * personal wall tunables above. Editing a Personal Settings value calls persist()
 * to save it locally, but since its key is absent here it produces no room diff.
 */
export const ROOM_SYNCED_SETTING_KEYS: readonly (keyof Settings)[] = [
  "gravityY",
  "clockPeriod",
  "features",
  "simpleMode",
];

/** The room-synced subset of a Settings object, the only settings mirrored to other room participants. */
export function roomSyncSettings(settings: Settings): Partial<Settings> {
  const out: Record<string, unknown> = {};
  for (const key of ROOM_SYNCED_SETTING_KEYS) {
    out[key] = settings[key];
  }
  return out as Partial<Settings>;
}

/**
 * Merges a loaded scene's setting block onto the current device settings: only
 * the scene-setting subset is taken from the scene, every editor / device-local
 * field keeps its current value. Garbage / type-mismatched scene values are
 * dropped by `sanitize`, so a scene can never corrupt a setting.
 */
export function applySceneSettings(current: Settings, sceneBlock: unknown): Settings {
  const merged: Settings = { ...current };
  if (typeof sceneBlock !== "object" || sceneBlock === null) {
    return merged;
  }
  const sanitized = sanitize(sceneBlock as Record<string, unknown>);
  for (const key of SCENE_SETTING_KEYS) {
    if (sanitized[key] !== undefined) {
      Object.assign(merged, { [key]: sanitized[key] });
    }
  }
  return reconcileSimplePlacement(merged);
}

/**
 * Enforces the Simple↔play-mode invariant on a loaded or merged settings object:
 * Simple and play mode (object placement off) are mutually exclusive, so a
 * legacy or hand-edited state carrying both is reconciled with Simple winning —
 * object placement is forced back on — before it reaches the editor.
 */
function reconcileSimplePlacement(s: Settings): Settings {
  if (s.simpleMode && !s.objectPlacement) {
    s.objectPlacement = true;
  }
  return s;
}

const ALLOWED_SHAPES: readonly string[] = ["box", "sphere", "funnel", "force", "rail", "calc", "count", "display"];
const ALLOWED_VIEW_MODES: readonly string[] = ["2d", "3d"];
const ALLOWED_COLOR_SETS: readonly string[] = COLOR_SETS;
const ALLOWED_MATERIALS: readonly string[] = OBJECT_MATERIALS;
const ALLOWED_PARTICLE_MATERIALS: readonly string[] = PARTICLE_MATERIALS;
const ALLOWED_EDIT_SNAP: readonly string[] = EDIT_SNAP_VALUES;
const ALLOWED_LANGUAGES: readonly string[] = ["en", "ja"];
const ALLOWED_WALL_SHADOW_RES: readonly number[] = [1024, 2048, 4096, 8192];

/**
 * Keep only fields whose stored value still matches the default's type (and for
 * enums, an allowed member); drop NaN/Infinity. Unknown / type-changed / garbage
 * fields are dropped so the default fills them, while every still-valid field is
 * preserved — adding or changing fields never wipes the rest of the user's tuning.
 */
function sanitize(values: Record<string, unknown>): Partial<Settings> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(DEFAULT_SETTINGS) as (keyof Settings)[]) {
    const v = values[key];
    const d = DEFAULT_SETTINGS[key];
    if (typeof v !== typeof d) {
      continue;
    }
    if (typeof v === "number" && !Number.isFinite(v)) {
      continue;
    }
    if (key === "shape" && !ALLOWED_SHAPES.includes(v as string)) {
      continue;
    }
    if (key === "viewMode" && !ALLOWED_VIEW_MODES.includes(v as string)) {
      continue;
    }
    if (key === "language" && !ALLOWED_LANGUAGES.includes(v as string)) {
      continue;
    }
    if (key === "colorSet" && !ALLOWED_COLOR_SETS.includes(v as string)) {
      continue;
    }
    if (key === "objectMaterial" && !ALLOWED_MATERIALS.includes(v as string)) {
      continue;
    }
    if (key === "particleMaterial" && !ALLOWED_PARTICLE_MATERIALS.includes(v as string)) {
      continue;
    }
    if (key === "editSnap" && !ALLOWED_EDIT_SNAP.includes(v as string)) {
      continue;
    }
    if (key === "foldouts" && (v === null || Array.isArray(v))) {
      continue;
    }
    if (key === "features") {
      if (v === null || Array.isArray(v)) {
        continue;
      }
      const src = v as Record<string, unknown>;
      const merged: Record<FeatureCategory, boolean> = { ...DEFAULT_SETTINGS.features };
      for (const cat of FEATURE_CATEGORIES) {
        if (typeof src[cat] === "boolean") {
          merged[cat] = src[cat] as boolean;
        }
      }
      out[key] = merged;
      continue;
    }
    if (key === "clockPeriod") {
      out[key] = Math.min(CLOCK_PERIOD_MAX, Math.max(CLOCK_PERIOD_MIN, v as number));
      continue;
    }
    if (key === "gravityY") {
      out[key] = Math.min(GRAVITY_Y_MAX, Math.max(GRAVITY_Y_MIN, v as number));
      continue;
    }
    if (key === "fallKillDistance") {
      out[key] = Math.min(FALL_KILL_DIST_MAX, Math.max(FALL_KILL_DIST_MIN, v as number));
      continue;
    }
    if (key === "fallKillLifeDivisor") {
      out[key] = Math.min(FALL_KILL_LIFE_DIV_MAX, Math.max(FALL_KILL_LIFE_DIV_MIN, v as number));
      continue;
    }
    if (key === "wallLightX" || key === "wallLightY") {
      out[key] = Math.min(1, Math.max(-1, v as number));
      continue;
    }
    if (key === "wallShadowRes") {
      if (!ALLOWED_WALL_SHADOW_RES.includes(v as number)) {
        continue;
      }
      out[key] = v;
      continue;
    }
    out[key] = v;
  }
  return out as Partial<Settings>;
}

/**
 * Saved data without a `features` key means a user from before the category
 * toggles existed: they get every category enabled so the migration never hides
 * UI they already had. Only a truly fresh load (no stored settings at all)
 * starts with the all-off simplified default.
 */
let benchSceneOverride: Record<string, unknown> | null = null;

/**
 * Bench mode (?bench=<fixture>) feeds a fixture scene straight into the live
 * loaders without writing localStorage, so a benchmark run never disturbs the
 * visitor's saved scene. When set, the load* functions return the fixture's
 * arrays / settings instead of persisted state; clearing it (null) restores
 * normal localStorage-backed loading.
 */
export function setBenchSceneOverride(scene: Record<string, unknown> | null): void {
  benchSceneOverride = scene;
}

/**
 * Picks the UI language for a visitor who has never saved one: ja for any
 * ja-* browser locale, en otherwise. Only consulted when localStorage has no
 * valid stored language, so an explicit choice always wins on later visits.
 */
function detectLanguage(): Language {
  return navigator.language.startsWith("ja") ? "ja" : "en";
}

export function loadSettings(): Settings {
  if (benchSceneOverride !== null) {
    return applySceneSettings({ ...DEFAULT_SETTINGS }, benchSceneOverride.settings);
  }
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return { ...DEFAULT_SETTINGS, language: detectLanguage() };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
  if (typeof parsed !== "object" || parsed === null) {
    return { ...DEFAULT_SETTINGS };
  }
  const obj = parsed as Record<string, unknown>;
  const rawValues =
    typeof obj.version === "number" && typeof obj.values === "object" && obj.values !== null
      ? (obj.values as Record<string, unknown>)
      : obj;
  const sanitized = sanitize(rawValues);
  if (sanitized.showNoteName === undefined) {
    if (typeof rawValues.noteNameMode === "string") {
      sanitized.showNoteName = rawValues.noteNameMode !== "off";
    } else if (typeof rawValues.showPitchLabels === "boolean") {
      sanitized.showNoteName = rawValues.showPitchLabels;
    }
  }
  if (rawValues.features === undefined) {
    sanitized.features = { physics: true, "3d": true, node: true };
  }
  if (sanitized.language === undefined) {
    sanitized.language = detectLanguage();
  }
  if (sanitized.communitySceneCameraEnabled === undefined) {
    sanitized.communitySceneCameraEnabled = true;
  }
  if (sanitized.communitySceneObjectPlacement === undefined) {
    sanitized.communitySceneObjectPlacement = true;
  }
  return reconcileSimplePlacement({ ...DEFAULT_SETTINGS, ...sanitized });
}

/**
 * While a bench fixture override is active every save* is a no-op: a benchmark
 * run drives the live scene but must leave the visitor's persisted scene
 * untouched, so no measurement run can ever overwrite localStorage.
 */
export function saveSettings(settings: Settings): void {
  if (benchSceneOverride !== null) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: SETTINGS_VERSION, values: settings }));
}

const OBJECTS_KEY = "resonella.objects";

/**
 * True when any scene state has been persisted (settings, objects, or
 * emitters). A fresh install / cleared storage returns false, which the
 * bootstrap uses to decide whether to seed the bundled default scene.
 */
export function hasSavedScene(): boolean {
  return (
    benchSceneOverride !== null ||
    localStorage.getItem(STORAGE_KEY) !== null ||
    localStorage.getItem(OBJECTS_KEY) !== null ||
    localStorage.getItem(EMITTERS_KEY) !== null
  );
}

export function saveObjects(data: unknown): void {
  if (benchSceneOverride !== null) return;
  localStorage.setItem(OBJECTS_KEY, JSON.stringify(data));
}

export function loadObjects(): unknown {
  if (benchSceneOverride !== null) {
    return Array.isArray(benchSceneOverride.objects) ? benchSceneOverride.objects : [];
  }
  const raw = localStorage.getItem(OBJECTS_KEY);
  if (!raw) {
    return [];
  }
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

const EMITTERS_KEY = "resonella.emitters";

export function saveEmitters(data: unknown): void {
  if (benchSceneOverride !== null) return;
  localStorage.setItem(EMITTERS_KEY, JSON.stringify(data));
}

const CONNECTIONS_KEY = "resonella.connections";

export function saveConnections(data: unknown): void {
  if (benchSceneOverride !== null) return;
  localStorage.setItem(CONNECTIONS_KEY, JSON.stringify(data));
}

export function loadConnections(): unknown {
  if (benchSceneOverride !== null) {
    return Array.isArray(benchSceneOverride.connections) ? benchSceneOverride.connections : [];
  }
  const raw = localStorage.getItem(CONNECTIONS_KEY);
  if (!raw) {
    return [];
  }
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

const ENV_KEY = "resonella.env";

/**
 * Validates a persisted / scene-bundle env blob into WallParams. Reads the
 * preset id and expands it to shared bake params. Returns null on a missing /
 * unknown preset (including legacy free-param blobs) so the caller falls back
 * to WALL_DEFAULT_PARAMS (a display default, not a hidden resolution of a
 * required object).
 */
export function parseWallParams(raw: unknown): WallParams | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const preset = (raw as Record<string, unknown>).preset;
  if (typeof preset !== "string" || !WALL_PRESETS.includes(preset as WallPreset)) {
    return null;
  }
  return wallPresetParams(preset as WallPreset);
}

export function saveEnv(params: WallParams): void {
  if (benchSceneOverride !== null) return;
  localStorage.setItem(ENV_KEY, JSON.stringify({ preset: wallParamsToPreset(params) }));
}

export function loadEnv(): WallParams {
  if (benchSceneOverride !== null) {
    return parseWallParams(benchSceneOverride.env) ?? { ...WALL_DEFAULT_PARAMS };
  }
  const raw = localStorage.getItem(ENV_KEY);
  if (!raw) {
    return { ...WALL_DEFAULT_PARAMS };
  }
  try {
    return parseWallParams(JSON.parse(raw)) ?? { ...WALL_DEFAULT_PARAMS };
  } catch {
    return { ...WALL_DEFAULT_PARAMS };
  }
}

const PENDING_CAMERA_KEY = "resonella.pendingCamera";

/**
 * A one-shot camera pose handed across the reload that a scene import / slot load
 * performs: persistScene writes the loaded scene's pose here, the next boot reads
 * it once and clears it, so a plain refresh afterwards keeps the default-per-mode
 * pose (a stored camera never sticks beyond the single load it belongs to).
 */
export function savePendingCamera(camera: unknown): void {
  if (benchSceneOverride !== null) return;
  if (camera === undefined || camera === null) {
    localStorage.removeItem(PENDING_CAMERA_KEY);
    return;
  }
  localStorage.setItem(PENDING_CAMERA_KEY, JSON.stringify(camera));
}

export function takePendingCamera(): unknown {
  const raw = localStorage.getItem(PENDING_CAMERA_KEY);
  localStorage.removeItem(PENDING_CAMERA_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function numField(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Reconstructs the pre-multi-emitter scene's single global emitter from the v3
 * settings blob (flat rate / directionDeg / coneDeg / speed fields), placing it
 * at the legacy fixed EMITTER_POSITION. Returns null when no such legacy field
 * is present, so a fresh install falls through to the default emitter instead.
 */
function legacyEmitter(): Record<string, number> | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const obj = parsed as Record<string, unknown>;
  const values =
    typeof obj.version === "number" && typeof obj.values === "object" && obj.values !== null
      ? (obj.values as Record<string, unknown>)
      : obj;
  const rate = numField(values.rate);
  const directionDeg = numField(values.directionDeg);
  const coneDeg = numField(values.coneDeg);
  const speedMin = numField(values.speedMin);
  const speedMax = numField(values.speedMax);
  const lifeMin = numField(values.lifeMin);
  const lifeMax = numField(values.lifeMax);
  if (
    rate === null && directionDeg === null && coneDeg === null &&
    speedMin === null && speedMax === null && lifeMin === null && lifeMax === null
  ) {
    return null;
  }
  return {
    x: EMITTER_POSITION.x,
    y: EMITTER_POSITION.y,
    z: EMITTER_POSITION.z,
    rate: rate ?? EMIT_RATE_DEFAULT,
    directionDeg: directionDeg ?? 0,
    coneDeg: coneDeg ?? EMIT_CONE_HALF_ANGLE * (180 / Math.PI),
    speedMin: speedMin ?? EMIT_SPEED_MIN,
    speedMax: speedMax ?? EMIT_SPEED_MAX,
    lifeMin: lifeMin ?? LIFESPAN_MIN,
    lifeMax: lifeMax ?? LIFESPAN_MAX,
  };
}

export function loadEmitters(): unknown {
  if (benchSceneOverride !== null) {
    return Array.isArray(benchSceneOverride.emitters) ? benchSceneOverride.emitters : [];
  }
  const raw = localStorage.getItem(EMITTERS_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }
  const legacy = legacyEmitter();
  return legacy ? [legacy] : [];
}

const SLOTS_KEY = "resonella.slots";
const SLOTS_VERSION = 1;

export type SlotEntry = {
  name: string;
  savedAt: string;
  scene: unknown;
  thumb?: string;
};

export function loadSlots(): SlotEntry[] {
  const raw = localStorage.getItem(SLOTS_KEY);
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (typeof parsed !== "object" || parsed === null) return [];
  const obj = parsed as Record<string, unknown>;
  if (!Array.isArray(obj.slots)) return [];
  return obj.slots.filter(
    (e): e is SlotEntry =>
      typeof e === "object" &&
      e !== null &&
      typeof (e as Record<string, unknown>).name === "string" &&
      typeof (e as Record<string, unknown>).savedAt === "string" &&
      "scene" in (e as Record<string, unknown>),
  );
}

export function saveSlot(name: string, scene: unknown, thumb?: string): string | null {
  const slots = loadSlots();
  const idx = slots.findIndex((s) => s.name === name);
  const entry: SlotEntry = { name, savedAt: new Date().toISOString(), scene, ...(thumb !== undefined ? { thumb } : {}) };
  if (idx >= 0) {
    slots[idx] = entry;
  } else {
    slots.push(entry);
  }
  try {
    localStorage.setItem(SLOTS_KEY, JSON.stringify({ version: SLOTS_VERSION, slots }));
    return null;
  } catch (e) {
    if (e instanceof DOMException && (e.name === "QuotaExceededError" || e.name === "NS_ERROR_DOM_QUOTA_REACHED")) {
      return "storage quota exceeded — delete some slots to free space";
    }
    return "save failed";
  }
}

export function deleteSlot(name: string): void {
  const slots = loadSlots().filter((s) => s.name !== name);
  localStorage.setItem(SLOTS_KEY, JSON.stringify({ version: SLOTS_VERSION, slots }));
}

const MIC_IMPORT_KEY = "resonella.micImport";

/**
 * The active imported-scene mic kit, persisted so a reload re-resolves the same
 * Layer B recordings (by content hash from the manifest, fetched from the
 * author's R2 objects under `owner`) instead of falling back to this device's own
 * library. Null / absent means the current scene plays this device's own
 * recordings (Layer A). `manifest` is stored opaque and validated on load.
 */
export function saveMicImport(state: { owner: string; manifest: unknown } | null): void {
  if (benchSceneOverride !== null) return;
  if (state === null) {
    localStorage.removeItem(MIC_IMPORT_KEY);
    return;
  }
  localStorage.setItem(MIC_IMPORT_KEY, JSON.stringify(state));
}

export function loadMicImport(): { owner: string; manifest: unknown } | null {
  const raw = localStorage.getItem(MIC_IMPORT_KEY);
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const obj = parsed as Record<string, unknown>;
  if (typeof obj.owner !== "string" || !Array.isArray(obj.manifest)) {
    return null;
  }
  return { owner: obj.owner, manifest: obj.manifest };
}

const MIC_DELETE_WARN_SUPPRESSED_KEY = "resonella.micDeleteWarnSuppressed";

/**
 * Whether the user has opted out of the warning shown before deleting a local
 * mic recording (deleting renumbers the remaining recordings, shifting the
 * index references of locally saved scenes). Persisted so "don't show again"
 * survives reloads.
 */
export function saveMicDeleteWarnSuppressed(suppressed: boolean): void {
  if (suppressed) {
    localStorage.setItem(MIC_DELETE_WARN_SUPPRESSED_KEY, "1");
  } else {
    localStorage.removeItem(MIC_DELETE_WARN_SUPPRESSED_KEY);
  }
}

export function loadMicDeleteWarnSuppressed(): boolean {
  return localStorage.getItem(MIC_DELETE_WARN_SUPPRESSED_KEY) === "1";
}

const SCENE_PROVENANCE_KEY = "resonella.sceneProvenance";

/**
 * The author of the scene currently committed to localStorage as "my current
 * scene", persisted so the foreign-scene guards (no re-upload / no slot save) and
 * the info chip survive a reload — without it, keep→reload would silently demote
 * someone else's scene to your own. Stored opaque (the scene-list meta plus its
 * mic manifest) and validated on load. Null / absent means an own scene.
 */
export function saveProvenance(state: { meta: Record<string, unknown>; micManifest: unknown } | null): void {
  if (benchSceneOverride !== null) return;
  if (state === null) {
    localStorage.removeItem(SCENE_PROVENANCE_KEY);
    return;
  }
  localStorage.setItem(SCENE_PROVENANCE_KEY, JSON.stringify(state));
}

export function loadProvenance(): { meta: Record<string, unknown>; micManifest: unknown } | null {
  const raw = localStorage.getItem(SCENE_PROVENANCE_KEY);
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const obj = parsed as Record<string, unknown>;
  if (typeof obj.meta !== "object" || obj.meta === null) {
    return null;
  }
  return { meta: obj.meta as Record<string, unknown>, micManifest: obj.micManifest ?? null };
}

const THUMB_KEY = "resonella.thumb";

export type ThumbEntry = {
  version: 1;
  dataUrl: string;
  capturedAt: string;
};

export function loadThumb(): ThumbEntry | null {
  const raw = localStorage.getItem(THUMB_KEY);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;
  if (obj["version"] !== 1 || typeof obj["dataUrl"] !== "string" || typeof obj["capturedAt"] !== "string") return null;
  return { version: 1, dataUrl: obj["dataUrl"] as string, capturedAt: obj["capturedAt"] as string };
}

export function saveThumb(dataUrl: string): void {
  const entry: ThumbEntry = { version: 1, dataUrl, capturedAt: new Date().toISOString() };
  localStorage.setItem(THUMB_KEY, JSON.stringify(entry));
}

const BOOKMARK_THUMB_KEY = "resonella.bookmarkThumbs";
const BOOKMARK_THUMB_CAP = 64;
const BOOKMARK_THUMB_W = 192;
const BOOKMARK_THUMB_H = 312;
const BOOKMARK_THUMB_QUALITY = 0.6;

type BookmarkThumbEntry = { id: string; dataUrl: string };

/**
 * Module-level in-memory copy of the store, lazily loaded once. saveBookmarkThumb
 * finishes asynchronously (after the source image decodes), so several saves can be
 * in flight at once; mutating this single shared array — rather than each re-reading
 * localStorage at completion time — keeps concurrent writes from clobbering one
 * another with a stale snapshot.
 */
let bookmarkThumbCache: BookmarkThumbEntry[] | null = null;

function bookmarkThumbEntries(): BookmarkThumbEntry[] {
  if (bookmarkThumbCache !== null) {
    return bookmarkThumbCache;
  }
  const raw = localStorage.getItem(BOOKMARK_THUMB_KEY);
  if (!raw) {
    bookmarkThumbCache = [];
    return bookmarkThumbCache;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    bookmarkThumbCache = [];
    return bookmarkThumbCache;
  }
  const obj = typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
  bookmarkThumbCache = !Array.isArray(obj.entries)
    ? []
    : obj.entries.filter(
        (e): e is BookmarkThumbEntry =>
          typeof e === "object" &&
          e !== null &&
          typeof (e as Record<string, unknown>).id === "string" &&
          typeof (e as Record<string, unknown>).dataUrl === "string",
      );
  return bookmarkThumbCache;
}

/** The persisted low-resolution objects-only still for a community scene, or null when none has been captured. */
export function loadBookmarkThumb(id: string): string | null {
  const found = bookmarkThumbEntries().find((e: BookmarkThumbEntry): boolean => e.id === id);
  return found === undefined ? null : found.dataUrl;
}

/**
 * Downscales an in-memory A2 still (the 320x518 objects-only feed preview) to a
 * low-resolution jpeg and persists it under the scene id, newest last. The store
 * is an insertion-ordered LRU capped at BOOKMARK_THUMB_CAP; on a quota error the
 * oldest entries are dropped and the write retried until it fits, then given up (a
 * missing bookmark still only falls back to the placeholder). Re-saving an id moves
 * it to the newest slot. The downscale runs after the source image decodes, so the
 * write is asynchronous.
 */
export function saveBookmarkThumb(id: string, sourceDataUrl: string): void {
  const img = new Image();
  img.onload = (): void => {
    const canvas = document.createElement("canvas");
    canvas.width = BOOKMARK_THUMB_W;
    canvas.height = BOOKMARK_THUMB_H;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;
    ctx.drawImage(img, 0, 0, BOOKMARK_THUMB_W, BOOKMARK_THUMB_H);
    const dataUrl = canvas.toDataURL("image/jpeg", BOOKMARK_THUMB_QUALITY);
    const entries = bookmarkThumbEntries();
    const existing = entries.findIndex((e: BookmarkThumbEntry): boolean => e.id === id);
    if (existing >= 0) {
      entries.splice(existing, 1);
    }
    entries.push({ id, dataUrl });
    while (entries.length > BOOKMARK_THUMB_CAP) entries.shift();
    persistBookmarkThumbs(entries);
  };
  img.src = sourceDataUrl;
}

function persistBookmarkThumbs(entries: BookmarkThumbEntry[]): void {
  while (true) {
    try {
      localStorage.setItem(BOOKMARK_THUMB_KEY, JSON.stringify({ version: 1, entries }));
      return;
    } catch (e) {
      if (!(e instanceof DOMException && (e.name === "QuotaExceededError" || e.name === "NS_ERROR_DOM_QUOTA_REACHED")) || entries.length === 0) {
        return;
      }
      entries.shift();
    }
  }
}
